import { useMemo, useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import QRCode from "qrcode";
import { MapContainer, TileLayer, Marker, Polyline, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Navigation, MapPin, Mountain, Waves, Droplets, Snowflake, Building2, Flame,
  Route as RouteIcon, ExternalLink, Plus, Check, X, TreePine, Fish, Sparkles, CircleDot,
  GripVertical, Share2, Copy, ChevronUp, ChevronDown, Download,
} from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

// The most worthy stops across the whole of Iceland, grouped by region.
const STOPS = [
  // Reykjavík & Reykjanes (Southwest)
  { id: "keflavik", name: "Keflavík", ice: "Airport", region: "Reykjavík & Reykjanes", type: "town", coords: [63.9850, -22.6056], desc: "Iceland's international gateway — most road trips start or end here, a harbour town on the Reykjanes peninsula." },
  { id: "bluelagoon", name: "Blue Lagoon", ice: "Bláa Lónið", region: "Reykjavík & Reykjanes", type: "geothermal", coords: [63.8804, -22.4495], desc: "Iceland's legendary geothermal spa — milky-blue, silica-rich water at a toasty 38–40 °C, set in a jet-black lava field." },
  { id: "reykjanes", name: "Reykjanes Geopark", ice: "Gunnuhver", region: "Reykjavík & Reykjanes", type: "geothermal", coords: [63.8190, -22.6840], desc: "Steaming mud pots, the Bridge Between Continents and recent volcanic eruption sites on a raw, otherworldly peninsula." },
  { id: "reykjavik", name: "Reykjavík", ice: "Höfuðborg", region: "Reykjavík & Reykjanes", type: "town", coords: [64.1466, -21.9426], desc: "The world's northernmost capital — Hallgrímskirkja, Harpa, colourful streets, hot dogs and the best nightlife in the North Atlantic." },

  // Golden Circle
  { id: "thingvellir", name: "Þingvellir National Park", ice: "Þingvellir", region: "Golden Circle", type: "park", coords: [64.2559, -21.1295], desc: "A UNESCO rift valley where the tectonic plates pull apart — birthplace of the world's oldest parliament. Snorkel the crystal Silfra fissure." },
  { id: "geysir", name: "Geysir & Strokkur", ice: "Haukadalur", region: "Golden Circle", type: "geothermal", coords: [64.3104, -20.3024], desc: "The geothermal field that gave the world the word 'geyser'. Strokkur erupts 15–20 m every few minutes." },
  { id: "gullfoss", name: "Gullfoss", ice: "Gullfoss", region: "Golden Circle", type: "waterfall", coords: [64.3271, -20.1199], desc: "The 'Golden Falls' — a thundering two-tiered cascade plunging into a rugged canyon, often wrapped in rainbows." },
  { id: "kerid", name: "Kerið Crater", ice: "Kerið", region: "Golden Circle", type: "crater", coords: [64.0411, -20.8855], desc: "A 3,000-year-old volcanic crater with vivid red slopes cradling a startling aquamarine lake." },

  // South Coast
  { id: "seljalandsfoss", name: "Seljalandsfoss", ice: "Seljalandsfoss", region: "South Coast", type: "waterfall", coords: [63.6156, -19.9886], desc: "A slender 60 m waterfall you can walk right behind — bring a rain jacket for the spray." },
  { id: "skogafoss", name: "Skógafoss", ice: "Skógafoss", region: "South Coast", type: "waterfall", coords: [63.5319, -19.5114], desc: "A mighty 60 m curtain of water; climb the staircase beside it for a sweeping view over the coast." },
  { id: "solheimajokull", name: "Sólheimajökull", ice: "Sólheimajökull", region: "South Coast", type: "glacier", coords: [63.5316, -19.3690], desc: "An accessible glacier tongue perfect for guided glacier hikes and ice-climbing." },
  { id: "dyrholaey", name: "Dyrhólaey", ice: "Dyrhólaey", region: "South Coast", type: "beach", coords: [63.4005, -19.1283], desc: "A dramatic promontory with a giant sea arch and a lighthouse — puffins nest here in summer." },
  { id: "reynisfjara", name: "Reynisfjara Black Beach", ice: "Reynisfjara", region: "South Coast", type: "beach", coords: [63.4027, -19.0448], desc: "Iceland's famous black-sand beach with towering basalt columns and the Reynisdrangar sea stacks. Beware the deadly sneaker waves." },
  { id: "vik", name: "Vík í Mýrdal", ice: "Vík", region: "South Coast", type: "town", coords: [63.4186, -19.0060], desc: "The cliff-top red church above a black beach — Iceland's southernmost village and a handy service stop." },
  { id: "fjadrargljufur", name: "Fjaðrárgljúfur Canyon", ice: "Fjaðrárgljúfur", region: "South Coast", type: "canyon", coords: [63.7713, -18.1725], desc: "A serpentine, moss-draped canyon up to 100 m deep carved by a glacial river." },
  { id: "skaftafell", name: "Skaftafell", ice: "Vatnajökull NP", region: "South Coast", type: "park", coords: [64.0159, -16.9666], desc: "A green oasis on Europe's largest glacier — hike to Svartifoss, framed by black basalt columns." },
  { id: "jokulsarlon", name: "Jökulsárlón Glacier Lagoon", ice: "Jökulsárlón", region: "South Coast", type: "glacier", coords: [64.0784, -16.2306], desc: "The crown jewel: a deep lagoon where house-sized icebergs calve and drift to the sea. Watch for seals." },
  { id: "diamondbeach", name: "Diamond Beach", ice: "Breiðamerkursandur", region: "South Coast", type: "beach", coords: [64.0438, -16.1760], desc: "Glittering ice chunks scattered across black sand like diamonds — right across the road from the lagoon." },
  { id: "hofn", name: "Höfn í Hornafirði", ice: "Höfn", region: "South Coast", type: "town", coords: [64.2538, -15.2122], desc: "A snug fishing harbour under the Vatnajökull ice cap, famous for langoustine ('Icelandic lobster')." },

  // East Iceland
  { id: "djupivogur", name: "Djúpivogur", ice: "Djúpivogur", region: "East Iceland", type: "town", coords: [64.6558, -14.2807], desc: "A tranquil 'slow town' of eider ducks, art and the Eggin í Gleðivík stone eggs along a pretty fjord." },
  { id: "egilsstadir", name: "Egilsstaðir", ice: "Egilsstaðir", region: "East Iceland", type: "town", coords: [65.2627, -14.3948], desc: "The hub of the east, on lake Lagarfljót — gateway to forests, waterfalls and the eastern fjords." },
  { id: "seydisfjordur", name: "Seyðisfjörður", ice: "Seyðisfjörður", region: "East Iceland", type: "town", coords: [65.2592, -14.0086], desc: "An impossibly photogenic fjord village with a blue church, a rainbow street and a tumbling waterfall road in." },

  // North Iceland
  { id: "dettifoss", name: "Dettifoss", ice: "Dettifoss", region: "North Iceland", type: "waterfall", coords: [65.8142, -16.3844], desc: "Europe's most powerful waterfall — 100 m wide and endlessly thundering in a raw canyon." },
  { id: "myvatn", name: "Lake Mývatn", ice: "Mývatn", region: "North Iceland", type: "geothermal", coords: [65.6039, -16.9961], desc: "A volcanic wonderland of pseudo-craters, lava pillars, bubbling mud and the blissful Mývatn Nature Baths." },
  { id: "godafoss", name: "Goðafoss", ice: "Goðafoss", region: "North Iceland", type: "waterfall", coords: [65.6829, -17.5503], desc: "The elegant, horseshoe-shaped 'Waterfall of the Gods' — wide, powerful and easy to reach off Route 1." },
  { id: "akureyri", name: "Akureyri", ice: "Akureyri", region: "North Iceland", type: "town", coords: [65.6885, -18.1262], desc: "The 'Capital of the North' — a lively fjord town with botanic gardens, cafés and heart-shaped red traffic lights." },
  { id: "husavik", name: "Húsavík", ice: "Húsavík", region: "North Iceland", type: "wildlife", coords: [66.0449, -17.3389], desc: "Iceland's whale-watching capital — humpbacks, minkes and even blue whales in Skjálfandi Bay." },

  // West & Snæfellsnes
  { id: "kirkjufell", name: "Kirkjufell", ice: "Grundarfjörður", region: "West & Snæfellsnes", type: "mountain", coords: [64.9427, -23.3074], desc: "Iceland's most photographed mountain — the 'church mountain' beside twin waterfalls on the Snæfellsnes peninsula." },
  { id: "snaefellsjokull", name: "Snæfellsjökull", ice: "Snæfellsnes NP", region: "West & Snæfellsnes", type: "glacier", coords: [64.8080, -23.7787], desc: "The glacier-capped volcano from Jules Verne's 'Journey to the Centre of the Earth', crowning a wild national park." },
  { id: "hraunfossar", name: "Hraunfossar", ice: "Hraunfossar", region: "West & Snæfellsnes", type: "waterfall", coords: [64.7026, -20.9792], desc: "A kilometre of crystal-clear water seeping from a lava field into the river — delicate and unique." },

  // Westfjords
  { id: "dynjandi", name: "Dynjandi", ice: "Dynjandi", region: "Westfjords", type: "waterfall", coords: [65.7340, -23.2048], desc: "The jewel of the Westfjords — a 100 m bridal-veil waterfall cascading down the mountainside in tiers." },
  { id: "latrabjarg", name: "Látrabjarg Cliffs", ice: "Látrabjarg", region: "Westfjords", type: "wildlife", coords: [65.5033, -24.5333], desc: "Europe's westernmost point and a vast seabird cliff — puffins at arm's length in summer." },
  { id: "isafjordur", name: "Ísafjörður", ice: "Ísafjörður", region: "Westfjords", type: "town", coords: [66.0749, -23.1356], desc: "The remote, mountain-ringed capital of the Westfjords — old timber houses, kayaking and dramatic fjord drives." },

  // Highlands (summer only)
  { id: "landmannalaugar", name: "Landmannalaugar", ice: "Fjallabak", region: "Highlands", type: "highland", coords: [63.9900, -19.0565], desc: "Rhyolite mountains in every colour, lava fields and natural hot springs — the start of the famous Laugavegur trek. (Summer / 4x4 only.)" },
  { id: "thorsmork", name: "Þórsmörk", ice: "Þórsmörk", region: "Highlands", type: "highland", coords: [63.6819, -19.5100], desc: "A lush glacial valley sheltered by three glaciers — a hiker's paradise reached by rugged F-roads. (Summer / 4x4 only.)" },
];

const REGIONS = [
  "Reykjavík & Reykjanes",
  "Golden Circle",
  "South Coast",
  "East Iceland",
  "North Iceland",
  "West & Snæfellsnes",
  "Westfjords",
  "Highlands",
];

const TYPE_META = {
  town: { label: "Town", color: "#C9A227", Icon: Building2 },
  geothermal: { label: "Geothermal", color: "#C44536", Icon: Flame },
  waterfall: { label: "Waterfall", color: "#4FA3C7", Icon: Droplets },
  glacier: { label: "Glacier", color: "#8CC5E3", Icon: Snowflake },
  beach: { label: "Beach", color: "#9AA3AB", Icon: Waves },
  canyon: { label: "Canyon", color: "#7FA86B", Icon: Mountain },
  crater: { label: "Crater", color: "#C56B3E", Icon: CircleDot },
  park: { label: "Nat. Park", color: "#6FB07A", Icon: TreePine },
  wildlife: { label: "Wildlife", color: "#5FB3A1", Icon: Fish },
  mountain: { label: "Mountain", color: "#A58CC9", Icon: Mountain },
  highland: { label: "Highlands", color: "#C98C5A", Icon: Sparkles },
};

const BY_ID = Object.fromEntries(STOPS.map((s) => [s.id, s]));

function haversineKm(a, b) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLon = toRad(b[1] - a[1]);
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function selectedIcon(n) {
  return L.divIcon({
    className: "",
    html: `<div style="width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#8CA8B9;border:2px solid #050505;display:flex;align-items:center;justify-content:center;box-shadow:0 3px 8px rgba(0,0,0,.6)"><span style="transform:rotate(45deg);color:#050505;font-weight:800;font-size:13px;font-family:sans-serif">${n}</span></div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
  });
}
function dotIcon(color) {
  return L.divIcon({
    className: "",
    html: `<div style="width:15px;height:15px;border-radius:50%;background:rgba(5,5,5,.65);border:2px solid ${color};box-shadow:0 0 0 2px rgba(5,5,5,.5)"></div>`,
    iconSize: [15, 15],
    iconAnchor: [7, 7],
  });
}

export default function RoadTrip() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState(() => {
    const r = searchParams.get("route");
    if (!r) return [];
    return r.split(",").filter((id) => BY_ID[id]);
  });
  const [shareOpen, setShareOpen] = useState(false);
  const [qr, setQr] = useState("");
  const [copied, setCopied] = useState(false);
  const dragIndex = useRef(null);

  const toggle = (id) =>
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const remove = (id) => setSelected((cur) => cur.filter((x) => x !== id));
  const clearAll = () => setSelected([]);

  const move = (from, to) =>
    setSelected((cur) => {
      if (to < 0 || to >= cur.length || from === to) return cur;
      const arr = [...cur];
      const [m] = arr.splice(from, 1);
      arr.splice(to, 0, m);
      return arr;
    });

  // keep the URL in sync so the current address is always the shareable route
  useEffect(() => {
    const params = new URLSearchParams();
    if (selected.length) params.set("route", selected.join(","));
    setSearchParams(params, { replace: true });
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const shareUrl = useMemo(() => {
    const base = `${window.location.origin}/road-trip`;
    return selected.length ? `${base}?route=${selected.join(",")}` : base;
  }, [selected]);

  // regenerate the QR whenever the share panel is open and the route changes
  useEffect(() => {
    if (!shareOpen) return;
    QRCode.toDataURL(shareUrl, { width: 640, margin: 1, errorCorrectionLevel: "M", color: { dark: "#0C0C0E", light: "#ffffff" } })
      .then(setQr)
      .catch(() => setQr(""));
  }, [shareOpen, shareUrl]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      /* clipboard blocked — the field is selectable as a fallback */
    }
  };

  const route = useMemo(() => {
    const stops = selected.map((id) => BY_ID[id]);
    const legs = stops.map((s, i) => (i === 0 ? 0 : haversineKm(stops[i - 1].coords, s.coords)));
    const totalKm = Math.round(legs.reduce((a, b) => a + b, 0));
    return { stops, legs, totalKm };
  }, [selected]);

  const positions = route.stops.map((s) => s.coords);
  const driveHours = route.totalKm > 0 ? (route.totalKm / 80) : 0;

  const googleUrl = useMemo(() => {
    if (route.stops.length < 2) return null;
    const path = route.stops.map((s) => `${s.coords[0]},${s.coords[1]}`).join("/");
    return `https://www.google.com/maps/dir/${path}`;
  }, [route.stops]);

  return (
    <div className="bg-[#050505] min-h-screen" data-testid="roadtrip-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16 px-6 overflow-hidden border-b border-white/10">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1504284769763-77a2d4b3d1a0?crop=entropy&cs=srgb&fm=jpg&q=80&w=1800)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/40" />
        <div className="relative max-w-6xl mx-auto">
          <motion.div {...fadeUp}>
            <div className="flex items-center gap-2 text-[#8CA8B9] uppercase tracking-[0.3em] text-xs mb-5">
              <RouteIcon size={15} /> Plan your Iceland journey
            </div>
            <h1 className="font-display font-extrabold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]">
              Build your own<br />Iceland road trip
            </h1>
            <p className="text-gray-300 text-base md:text-lg mt-6 max-w-2xl leading-relaxed">
              Tap the stops you want to visit — the most worthy sights across the whole island. We'll
              drop them on the map, draw your route and tally up the kilometres. When you're happy, open
              the whole itinerary in Google Maps for turn-by-turn directions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main: map + builder */}
      <section className="max-w-6xl mx-auto px-6 py-14 grid lg:grid-cols-[1fr_380px] gap-10 items-start">
        {/* Left: stop picker by region */}
        <div className="order-2 lg:order-1">
          <div className="flex items-center gap-2 mb-6">
            <MapPin size={18} className="text-[#8CA8B9]" />
            <h2 className="font-display font-bold uppercase tracking-tight text-2xl text-white">Choose your stops</h2>
          </div>
          {REGIONS.map((region) => {
            const stops = STOPS.filter((s) => s.region === region);
            return (
              <div key={region} className="mb-10" data-testid={`region-${region.replace(/[^a-zA-Z]/g, "-")}`}>
                <h3 className="text-[11px] uppercase tracking-[0.25em] text-gray-500 border-b border-white/10 pb-2 mb-4">{region}</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {stops.map((s) => {
                    const meta = TYPE_META[s.type];
                    const Ic = meta.Icon;
                    const idx = selected.indexOf(s.id);
                    const isSel = idx >= 0;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => toggle(s.id)}
                        data-testid={`stop-toggle-${s.id}`}
                        aria-pressed={isSel}
                        className={`text-left p-4 border transition-all duration-200 group relative ${
                          isSel
                            ? "border-[#8CA8B9] bg-[#8CA8B9]/10"
                            : "border-white/10 bg-[#0C0C0E] hover:border-white/30"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] mb-1" style={{ color: meta.color }}>
                              <Ic size={12} /> {meta.label}
                            </div>
                            <h4 className="font-display font-bold uppercase tracking-tight text-base text-white leading-tight truncate">{s.name}</h4>
                          </div>
                          <span
                            className={`shrink-0 w-7 h-7 flex items-center justify-center border transition-colors ${
                              isSel ? "bg-[#8CA8B9] border-[#8CA8B9] text-[#050505]" : "border-white/20 text-gray-400 group-hover:border-white/50"
                            }`}
                            data-testid={`stop-state-${s.id}`}
                          >
                            {isSel ? <Check size={15} /> : <Plus size={15} />}
                          </span>
                        </div>
                        <p className="text-gray-400 text-xs mt-2 leading-relaxed line-clamp-2">{s.desc}</p>
                        {isSel && (
                          <span className="absolute -top-2 -left-2 w-6 h-6 bg-[#8CA8B9] text-[#050505] font-display font-extrabold text-xs flex items-center justify-center">
                            {idx + 1}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Right: sticky map + itinerary */}
        <div className="order-1 lg:order-2 lg:sticky lg:top-24 space-y-5">
          <div className="border border-white/10 overflow-hidden relative z-0" data-testid="roadtrip-map">
            <MapContainer center={[64.9, -18.6]} zoom={6} scrollWheelZoom={false} style={{ height: "380px", width: "100%", background: "#050505" }}>
              <TileLayer
                attribution='Tiles &copy; Esri — Source: Esri, Maxar, Earthstar Geographics'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                maxZoom={18}
              />
              {positions.length >= 2 && (
                <Polyline positions={positions} pathOptions={{ color: "#F2D074", weight: 3.5, opacity: 0.95, dashArray: "2 9", lineCap: "round", lineJoin: "round" }} />
              )}
              {STOPS.map((s) => {
                const idx = selected.indexOf(s.id);
                const isSel = idx >= 0;
                return (
                  <Marker
                    key={s.id}
                    position={s.coords}
                    icon={isSel ? selectedIcon(idx + 1) : dotIcon(TYPE_META[s.type].color)}
                    zIndexOffset={isSel ? 1000 : 0}
                    eventHandlers={{ click: () => toggle(s.id) }}
                  >
                    <Tooltip direction="top" offset={[0, isSel ? -28 : -8]}>{s.name}</Tooltip>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>

          {/* Itinerary */}
          <div className="border border-white/10 bg-[#0C0C0E] p-5" data-testid="itinerary-panel">
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500">Your route</div>
                <div className="font-display font-extrabold text-3xl text-white leading-none mt-1" data-testid="itinerary-total-km">
                  {route.totalKm}<span className="text-base text-gray-400 ml-1">km</span>
                </div>
              </div>
              <div className="text-right text-xs text-gray-400">
                <div data-testid="itinerary-count">{route.stops.length} stop{route.stops.length === 1 ? "" : "s"}</div>
                {route.totalKm > 0 && <div className="mt-0.5">≈ {driveHours < 1 ? `${Math.round(driveHours * 60)} min` : `${driveHours.toFixed(1)} h`} driving</div>}
              </div>
            </div>

            {route.stops.length === 0 ? (
              <p className="text-gray-500 text-sm py-4 text-center border border-dashed border-white/10">
                Tap a stop to start building your route.
              </p>
            ) : (
              <ol className="space-y-1.5 mb-4 max-h-64 overflow-auto pr-1" data-testid="itinerary-list">
                {route.stops.map((s, i) => (
                  <li
                    key={s.id}
                    className="flex items-center gap-2 group bg-[#0C0C0E] border border-transparent hover:border-white/10 px-1 py-1"
                    data-testid={`itinerary-item-${s.id}`}
                    draggable
                    onDragStart={() => { dragIndex.current = i; }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { move(dragIndex.current, i); dragIndex.current = null; }}
                  >
                    <GripVertical size={14} className="shrink-0 text-gray-600 cursor-grab active:cursor-grabbing" data-testid={`itinerary-grip-${s.id}`} />
                    <span className="shrink-0 w-6 h-6 bg-[#8CA8B9]/15 text-[#8CA8B9] font-display font-bold text-xs flex items-center justify-center">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{s.name}</div>
                      {i > 0 && <div className="text-[11px] text-gray-500">+{Math.round(route.legs[i])} km from previous</div>}
                    </div>
                    <div className="shrink-0 flex flex-col">
                      <button onClick={() => move(i, i - 1)} disabled={i === 0} data-testid={`itinerary-up-${s.id}`} className="text-gray-600 hover:text-white disabled:opacity-25 disabled:hover:text-gray-600 transition-colors leading-none" title="Move up"><ChevronUp size={14} /></button>
                      <button onClick={() => move(i, i + 1)} disabled={i === route.stops.length - 1} data-testid={`itinerary-down-${s.id}`} className="text-gray-600 hover:text-white disabled:opacity-25 disabled:hover:text-gray-600 transition-colors leading-none" title="Move down"><ChevronDown size={14} /></button>
                    </div>
                    <button onClick={() => remove(s.id)} data-testid={`itinerary-remove-${s.id}`} className="shrink-0 text-gray-600 hover:text-[#C44536] transition-colors" title="Remove">
                      <X size={15} />
                    </button>
                  </li>
                ))}
              </ol>
            )}

            <div className="flex gap-2">
              <a
                href={googleUrl || undefined}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="open-route-google"
                aria-disabled={!googleUrl}
                onClick={(e) => { if (!googleUrl) e.preventDefault(); }}
                className={`flex-1 inline-flex items-center justify-center gap-2 uppercase tracking-[0.12em] text-[11px] font-bold py-3 transition-colors ${
                  googleUrl ? "bg-[#8CA8B9] text-[#050505] hover:bg-white" : "bg-white/5 text-gray-600 cursor-not-allowed"
                }`}
              >
                <Navigation size={14} /> Open in Google Maps
              </a>
              {route.stops.length > 0 && (
                <button onClick={clearAll} data-testid="clear-route" className="px-4 border border-white/15 text-gray-400 hover:border-white/40 hover:text-white uppercase tracking-[0.12em] text-[11px] font-bold transition-colors">
                  Clear
                </button>
              )}
            </div>

            {route.stops.length > 0 && (
              <button
                onClick={() => setShareOpen((o) => !o)}
                data-testid="share-route-toggle"
                className="w-full mt-2 inline-flex items-center justify-center gap-2 border border-white/15 text-gray-300 hover:border-[#8CA8B9] hover:text-white uppercase tracking-[0.12em] text-[11px] font-bold py-3 transition-colors"
              >
                <Share2 size={14} /> {shareOpen ? "Hide share link" : "Share this route"}
              </button>
            )}

            {shareOpen && route.stops.length > 0 && (
              <div className="mt-3 border border-white/10 bg-[#050505] p-4" data-testid="share-panel">
                <p className="text-[11px] uppercase tracking-[0.15em] text-gray-500 mb-2">Send this to your travel companions</p>
                <div className="flex gap-2 mb-4">
                  <input
                    readOnly
                    value={shareUrl}
                    data-testid="share-url-input"
                    onFocus={(e) => e.target.select()}
                    className="flex-1 min-w-0 bg-[#0C0C0E] border border-white/15 text-gray-300 text-xs px-3 py-2 focus:outline-none focus:border-[#8CA8B9]"
                  />
                  <button onClick={copyLink} data-testid="share-copy" className="shrink-0 inline-flex items-center gap-1.5 bg-[#8CA8B9] text-[#050505] hover:bg-white uppercase tracking-[0.1em] text-[10px] font-bold px-3 transition-colors">
                    {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
                  </button>
                </div>
                {qr && (
                  <div className="flex flex-col items-center">
                    <img src={qr} alt="Route QR code" data-testid="share-qr" className="w-40 h-40 border border-white/10" />
                    <a href={qr} download="iceland-route-qr.png" data-testid="share-qr-download" className="mt-3 inline-flex items-center gap-1.5 text-gray-400 hover:text-white uppercase tracking-[0.12em] text-[10px] font-bold transition-colors">
                      <Download size={12} /> Download QR
                    </a>
                  </div>
                )}
              </div>
            )}

            {route.stops.length === 1 && (
              <p className="text-[11px] text-gray-500 mt-2 text-center">Add one more stop to draw a route.</p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
